// Videos added by pasting a link (F-01.13, D-44, D-45): moonland generations and uploads, shared Google Drive files,
// and YouTube videos (downloaded by the local helper, docs/helper.md). Pure logic:
// which links are recognised, where each video is downloaded from, and the file names of downloaded copies.

export type RemoteKind = 'moonland' | 'gdrive' | 'youtube';

export interface RemoteRef {
  kind: RemoteKind;
  id: string;
  /** moonland: an uploaded file (reference video, original footage) rather than a generation. */
  upload?: boolean;
  /**
   * moonland: the folder of a generation's files, named after the model's maker ("topaz" for Topaz upscales);
   * absent means "bytedance", the folder of most video generations.
   */
  provider?: string;
}

/** Hosts the application downloads from (also listed in the CSP connect-src and scripts/check-privacy.mjs). */
export const REMOTE_ORIGINS = ['https://r2.moonland.ai', 'https://drive.usercontent.google.com'] as const;
/** The local helper that downloads YouTube videos with yt-dlp (D-45); only on this computer. */
export const HELPER_ORIGIN = 'http://127.0.0.1:47821';

/** moonland generation ids are cuids: "c" and 24 lowercase letters or digits. */
const MOONLAND_ID = /^c[a-z0-9]{24}$/;
/** moonland upload ids (reference videos): 24 lowercase letters or digits. */
const UPLOAD_ID = /^[a-z0-9]{20,32}$/;
/** moonland generation folders: "bytedance", "topaz", "kling"… */
const PROVIDER = /^[a-z0-9-]{2,32}$/;
/** Drive file ids: 25 or more URL-safe characters. */
const DRIVE_ID = /^[A-Za-z0-9_-]{25,}$/;
/** YouTube video ids: 11 URL-safe characters. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** URL a video is downloaded from (YouTube: the helper's download request, then its job endpoints). */
export function remoteUrl(r: RemoteRef): string {
  if (r.kind === 'youtube') return `${HELPER_ORIGIN}/v1/youtube?id=${encodeURIComponent(r.id)}`;
  if (r.kind === 'moonland') return `${REMOTE_ORIGINS[0]}/${r.upload ? 'upload' : (r.provider ?? 'bytedance')}/${r.id}/video.mp4`;
  // confirm=t skips the "can't scan this large file for viruses" page.
  return `${REMOTE_ORIGINS[1]}/download?id=${encodeURIComponent(r.id)}&export=download&confirm=t`;
}

const EXT_BY_TYPE: Record<string, string> = {
  'video/mp4': '.mp4',
  'video/quicktime': '.mov',
  'video/webm': '.webm',
  'video/x-matroska': '.mkv',
  'video/x-m4v': '.m4v',
};

/** moonland names a generation's video video.mp4 or, for some models (Kling), output.mp4. */
const MOONLAND_VIDEO_FILES = ['video.mp4', 'output.mp4'];
/** Folders tried, in order, for a generation whose folder is not known (a pasted page link). */
const MOONLAND_FOLDERS = ['bytedance', 'kling', 'topaz'];

/**
 * Addresses to try in order for a video: one for most links; for a moonland generation each likely file name,
 * and each likely folder when the folder is not known.
 */
export function remoteUrls(r: RemoteRef): string[] {
  if (r.kind !== 'moonland' || r.upload) return [remoteUrl(r)];
  const folders = r.provider ? [r.provider] : MOONLAND_FOLDERS;
  return folders.flatMap((p) => MOONLAND_VIDEO_FILES.map((f) => `${REMOTE_ORIGINS[0]}/${p}/${r.id}/${f}`));
}

/** File name of a downloaded copy; the link is read back from it when the file is opened later. */
export function remoteFileName(r: RemoteRef, contentType = 'video/mp4'): string {
  const ext = EXT_BY_TYPE[contentType.split(';')[0].trim().toLowerCase()] ?? '.mp4';
  return `${r.kind}_${r.upload ? 'upload_' : r.provider ? r.provider + '_' : ''}${r.id}${ext}`;
}

/** A moonland generation, keeping its folder unless it is the default one. */
function generation(id: string, provider?: string): RemoteRef {
  return provider && provider !== 'bytedance' && provider !== 'upload' && PROVIDER.test(provider) ? { kind: 'moonland', id, provider } : { kind: 'moonland', id };
}

/** Link of a downloaded copy from its name, also after the browser renamed a duplicate ("… (1).mp4"). */
export function remoteFromName(name: string): RemoteRef | null {
  const m = /^(moonland|gdrive|youtube)_([A-Za-z0-9_-]+?)(?: \(\d+\))?\.[a-z0-9]+$/i.exec(name);
  if (!m) return null;
  const kind = m[1].toLowerCase() as RemoteKind;
  if (kind === 'moonland') {
    if (MOONLAND_ID.test(m[2])) return { kind, id: m[2] };
    const up = /^upload_(.+)$/.exec(m[2]);
    if (up) return UPLOAD_ID.test(up[1]) ? { kind, id: up[1], upload: true } : null;
    // moonland_<folder>_<id>: a generation outside the default folder (a Topaz upscale…).
    const pv = /^([a-z0-9-]+)_(c[a-z0-9]{24})$/.exec(m[2]);
    return pv && PROVIDER.test(pv[1]) ? generation(pv[2], pv[1]) : null;
  }
  if (kind === 'youtube') return YOUTUBE_ID.test(m[2]) ? { kind, id: m[2] } : null;
  return DRIVE_ID.test(m[2]) ? { kind, id: m[2] } : null;
}

export type LinkResult = { ok: true; ref: RemoteRef } | { ok: false; reason: 'youtubeList' | 'driveFolder' | 'unknown'; text: string };

function isHost(u: URL, host: string): boolean {
  return u.hostname === host || u.hostname.endsWith('.' + host);
}

/** One pasted link. A bare cuid is taken as a moonland generation id. */
export function parseLink(token: string): LinkResult {
  const text = token.trim();
  if (MOONLAND_ID.test(text)) return { ok: true, ref: { kind: 'moonland', id: text } };
  let u: URL;
  try {
    u = new URL(text);
  } catch {
    return { ok: false, reason: 'unknown', text };
  }
  const parts = u.pathname.split('/').filter(Boolean);
  if (isHost(u, 'moonland.ai')) {
    const g = u.searchParams.get('g');
    if (g && MOONLAND_ID.test(g)) return { ok: true, ref: { kind: 'moonland', id: g } };
    const gen = parts.indexOf('generation');
    if (gen >= 0 && MOONLAND_ID.test(parts[gen + 1] ?? '')) return { ok: true, ref: { kind: 'moonland', id: parts[gen + 1] } };
    // Media URLs: /<provider>/<id>/video.mp4 for generations, /upload/<id>/… for uploaded reference videos (the
    // thumbnail's "copy image address" gives /upload/<id>/image.webp; the video is next to it).
    const id = parts[parts.length - 2] ?? '';
    if (parts.length === 3 && parts[0] === 'upload' && UPLOAD_ID.test(id)) return { ok: true, ref: { kind: 'moonland', id, upload: true } };
    // /<folder>/<id>/<file>: the generation's folder is kept (Topaz upscales are under /topaz/).
    if (MOONLAND_ID.test(id)) return { ok: true, ref: generation(id, parts.length === 3 && isHost(u, 'r2.moonland.ai') ? parts[0] : undefined) };
  }
  if (isHost(u, 'drive.google.com') || isHost(u, 'docs.google.com') || isHost(u, 'drive.usercontent.google.com')) {
    if (parts.includes('folders')) return { ok: false, reason: 'driveFolder', text };
    const d = parts.indexOf('d');
    const id = d >= 0 ? parts[d + 1] : u.searchParams.get('id');
    if (id && DRIVE_ID.test(id)) return { ok: true, ref: { kind: 'gdrive', id } };
  }
  if (isHost(u, 'youtu.be') && YOUTUBE_ID.test(parts[0] ?? '')) return { ok: true, ref: { kind: 'youtube', id: parts[0] } };
  if (isHost(u, 'youtube.com') || isHost(u, 'youtube-nocookie.com')) {
    const v = u.searchParams.get('v');
    if (v && YOUTUBE_ID.test(v)) return { ok: true, ref: { kind: 'youtube', id: v } };
    // /shorts/<id>, /embed/<id>, /live/<id>, /v/<id>
    if (['shorts', 'embed', 'live', 'v'].includes(parts[0]) && YOUTUBE_ID.test(parts[1] ?? '')) return { ok: true, ref: { kind: 'youtube', id: parts[1] } };
    // A playlist or channel without a single video.
    return { ok: false, reason: 'youtubeList', text };
  }
  return { ok: false, reason: 'unknown', text };
}

/** Links in pasted text (separated by spaces, lines or commas), in order, without repeats. */
export function parseLinks(text: string): { refs: RemoteRef[]; rejected: Extract<LinkResult, { ok: false }>[] } {
  const refs: RemoteRef[] = [];
  const rejected: Extract<LinkResult, { ok: false }>[] = [];
  for (const token of text.split(/[\s,，、]+/)) {
    if (!token) continue;
    const r = parseLink(token);
    if (!r.ok) rejected.push(r);
    else if (!refs.some((x) => x.kind === r.ref.kind && x.id === r.ref.id)) refs.push(r.ref);
  }
  return { refs, rejected };
}

/**
 * A video dragged from a moonland page (F-01.13). moonland puts its own types on the drag: a generation carries
 * `moonland/generation-reference` (its id) and a snapshot with the video URL; an uploaded reference video or
 * library item carries `moonland/resource-video-id` (its upload id); a picture only `moonland/resource-image-id`.
 * Links (an image address, a page URL) dragged from anywhere are read as pasted text.
 */
export function refFromDrag(get: (type: string) => string, types: readonly string[]): { ref: RemoteRef } | { image: true } | null {
  // The generation's snapshot first: it has the exact video address (Topaz upscales are not in the default
  // folder) and tells a picture generation apart, which has no video.
  for (const t of ['moonland/generation-comparison-snapshot', 'moonland/generation-merger-snapshot']) {
    if (!types.includes(t)) continue;
    try {
      const snap = JSON.parse(get(t)) as { previewResource?: { type?: string; videoUrl?: string | null } };
      const r = snap.previewResource?.videoUrl ? parseLink(snap.previewResource.videoUrl) : null;
      if (r?.ok) return { ref: r.ref };
      if (snap.previewResource?.type === 'IMAGE') return { image: true };
    } catch {
      /* not JSON */
    }
  }
  const gen = get('moonland/generation-reference').trim();
  if (MOONLAND_ID.test(gen)) return { ref: { kind: 'moonland', id: gen } };
  // Links carried by the drag: the dragged thumbnail's address (text/uri-list, or the <img> in text/html).
  const html = get('text/html');
  const linkText = [
    ...get('text/uri-list')
      .split('\n')
      .filter((l) => !l.startsWith('#')),
    ...[...html.matchAll(/(?:src|href)="([^"]+)"/g)].map((m) => m[1].replace(/&amp;/g, '&')),
    get('text/plain'),
  ].join(' ');
  const { refs } = parseLinks(linkText);
  const up = get('moonland/resource-video-id').trim();
  if (types.includes('moonland/resource-video-id')) {
    // A video resource: a generation used as a source ("草稿影片", a reference video marked 來源) has a resource
    // id that is not an upload; its thumbnail address names the generation. Uploads are found either way.
    const moon = refs.find((r) => r.kind === 'moonland');
    if (moon) return { ref: moon };
    if (UPLOAD_ID.test(up)) return { ref: { kind: 'moonland', id: up, upload: true } };
  }
  if (types.includes('moonland/resource-image-id')) return { image: true };
  return refs.length ? { ref: refs[0] } : null;
}

/** Whether pasted text looks like links meant for the application (so a paste outside text fields is handled). */
export function looksLikeMediaLinks(text: string): boolean {
  const { refs, rejected } = parseLinks(text);
  return refs.length > 0 || rejected.some((r) => r.reason !== 'unknown');
}
